import React, {useEffect, useState} from 'react';
import {styled} from 'baseui';
import {graphql, useStaticQuery, Link} from 'gatsby';
import {renderRichText} from 'gatsby-source-contentful/rich-text';
import {BLOCKS, INLINES} from '@contentful/rich-text-types';
import {Button, SHAPE, SIZE} from 'baseui/button';
import useLocalStorageState from 'use-local-storage-state';
import {Theme} from 'baseui/theme';

const FixedWrapper = styled('div', ({$theme}) => ({
  bottom: '0px',
  width: '100%',
  position: 'fixed',
  backgroundColor: $theme.colors.backgroundPrimary,
  zIndex: 99,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0px -5px 16px -9px rgba(0,0,0,0.17)',
}));

const Content = styled('div', ({$theme}) => ({
  maxWidth: '1200px',
  margin: '20px 16px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  [$theme.mediaQuery.small]: {
    margin: '10px 2px 10px 2px',
    justifyContent: 'flex-start',
    width: 'calc(100% - 32px)',
  },
}));

const StyledLink = styled(Link, ({$theme}) => ({
  color: $theme.colors.primary,
  textDecoration: 'underline',
  fontWeight: 700,
  marginBottom: '0px',
  textAlign: 'center',
  fontSize: '14px',
  [$theme.mediaQuery.small]: {
    fontSize: '12px',
  },
}));

const Paragraph = styled('p', ({$theme}) => ({
  marginBottom: '0px',
  fontSize: '14px',
  [$theme.mediaQuery.small]: {
    fontSize: '12px',
    maxWidth: '200px',
  },
}));

const StyledCtaButton = ($theme: Theme) => ({
  backgroundColor: $theme.colors.black,
  color: $theme.colors.backgroundPrimary,
  height: '24px',
  marginLeft: '16px',
  minWidth: '120px',
  fontWeight: 700,
  ':hover': {
    color: $theme.colors.backgroundPrimary,
  },
});

const renderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => (
      <Paragraph>{children}</Paragraph>
    ),
    [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => (
      <StyledLink to={node.data.uri}>{children}</StyledLink>
    ),
  },
};

export default function Cookiebanner() {
  const [haveAccepted, setHaveAccepted] = useLocalStorageState(
    'cookies-accepted-request',
    {defaultValue: false}
  );

  const [didRender, setDidRender] = useState(false);
  const data = useStaticQuery(graphql`
    query CookieBannerQuery {
      contentfulHeaderAndFooter {
        cookiebannerOkButton
        cookiebannerText {
          raw
        }
      }
    }
  `);

  const {cookiebannerOkButton, cookiebannerText} =
    data.contentfulHeaderAndFooter;

  const onHidePress = () => {
    setHaveAccepted(true);
  };

  useEffect(() => {
    setDidRender(true);
  }, []);

  if (haveAccepted || typeof haveAccepted === 'undefined' || !didRender)
    return null;

  return (
    <FixedWrapper>
      <Content>
        {renderRichText(cookiebannerText, renderOptions)}
        <Button
          overrides={{
            BaseButton: {
              style: ({$theme}) => StyledCtaButton($theme),
            },
          }}
          size={SIZE.mini}
          shape={SHAPE.pill}
          onClick={onHidePress}
        >
          {cookiebannerOkButton}
        </Button>
      </Content>
    </FixedWrapper>
  );
}
